import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Calendar, Users, Scissors, Package,
  CreditCard, ChevronDown, Search, Bell, Menu,
  ClipboardList, BarChart3, Building2,
  Shield, ShieldCheck, ScrollText, BellRing,
  Home, LogOut, UserCircle, Settings, Zap,
  FileSignature, FlaskConical, Landmark, Armchair,
  Monitor, Megaphone, Star, ClipboardCheck, Truck, Bot,
  BookOpen, Stethoscope,
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useLabNotifications } from '../hooks/useLabNotifications'
import api from '../lib/api'
import { CommandPalette } from '../components/CommandPalette'
import { Skeleton } from '../components/ui/Skeleton'
import { AssistantChat } from '../components/AssistantChat'
import { ReviewAlert } from '../components/reputation/ReviewAlert'
import { LowStockGlobalAlert } from '../components/inventory/LowStockGlobalAlert'
import { ContextualHelpButton } from '../tutorials/components/ContextualHelpButton'
const menuSections = [
  {
    label: 'Agenda',
    items: [
      { icon: Calendar, label: 'Calendario', path: '/appointments', permission: 'appointments:read' },
      { icon: ClipboardList, label: 'Lista de espera', path: '/waitlist', permission: 'waitlist:read' },
    ],
  },
  {
    label: 'Clinica',
    items: [
      { icon: Users, label: 'Pacientes', path: '/patients', permission: 'patients:read' },
      { icon: Scissors, label: 'Tratamientos', path: '/treatments', badgeKey: 'treatments', permission: 'treatments:read' },
      { icon: FileSignature, label: 'Consentimientos', path: '/consents', permission: 'consents:read' },
      { icon: FlaskConical, label: 'Laboratorio', path: '/lab', permission: 'lab:read' },
      { icon: Calendar, label: 'Recall', path: '/patients/recall', permission: 'patients:read' },
    ],
  },
  {
    label: 'Finanzas',
    items: [
      { icon: CreditCard, label: 'Facturacion', path: '/billing', permission: 'billing:read' },
      { icon: BarChart3, label: 'Presupuestos', path: '/budgets', permission: 'budgets:read' },
      { icon: Stethoscope, label: 'Catalogo', path: '/procedure-catalog', permission: 'budgets:read' },
      { icon: Shield, label: 'Seguros', path: '/insurance', permission: 'insurance:read' },
      { icon: Landmark, label: 'Contabilidad', path: '/accounting', permission: 'accounting:read' },
    ],
  },
  {
    label: 'Operaciones',
    items: [
      { icon: Package, label: 'Stock', path: '/inventory', badgeKey: 'inventory', permission: 'inventory:read' },
      { icon: Armchair, label: 'Recursos', path: '/resources', permission: 'resources:read' },
      { icon: Truck, label: 'Proveedores', path: '/suppliers', permission: 'procurement:read' },
    ],
  },
  {
    label: 'Comunicacion',
    items: [
      { icon: BellRing, label: 'Recordatorios', path: '/reminders', permission: 'reminders:read' },
      { icon: Zap, label: 'Automatizaciones', path: '/automations', permission: 'users:manage' },
      { icon: Bot, label: 'Agentes', path: '/agents', permission: 'users:manage' },
      { icon: Monitor, label: 'Recepcion', path: '/reception/checkin', permission: 'reception:manage' },
      { icon: Megaphone, label: 'Marketing', path: '/marketing', permission: 'marketing:read' },
    ],
  },
  {
    label: 'Analitica',
    items: [
      { icon: BarChart3, label: 'Informes', path: '/reports', permission: 'analytics:read' },
      { icon: ClipboardCheck, label: 'KPIs Clinicos', path: '/clinical-kpi', permission: 'clinical_kpis:read' },
      { icon: Star, label: 'Reputacion', path: '/reputation', permission: 'reputation:read' },
    ],
  },
  {
    label: 'Administracion',
    items: [
      { icon: Building2, label: 'Clinicas', path: '/clinics', permission: 'clinics:read' },
      { icon: Users, label: 'Usuarios', path: '/users', permission: 'users:read' },
      { icon: ShieldCheck, label: 'Permisos', path: '/permissions', permission: 'users:manage' },
      { icon: ScrollText, label: 'Auditoria', path: '/audit', permission: 'users:manage' },
    ],
  },
  {
    label: 'Ayuda',
    items: [
      { icon: BookOpen, label: 'Tutoriales', path: '/tutorials', permission: 'appointments:read' },
    ],
  },
]

export function AppLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, hasPermission, isReadOnly } = useAuthStore()

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const isTyping = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((v) => !v)
        return
      }

      if (!isTyping && !paletteOpen && e.key.toLowerCase() === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault()
        navigate('/appointments?new=1')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate, paletteOpen])

  useEffect(() => {
    setNotifOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  const userInitials = user
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : '??'

  const visibleSections = menuSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => hasPermission(item.permission)),
    }))
    .filter((section) => section.items.length > 0)

  const { data: currentClinic } = useQuery({
    queryKey: ['my-clinic'],
    queryFn: async () => {
      const { data } = await api.get('/clinics/my-clinic')
      return data
    },
    enabled: !!user?.clinicId,
  })

  const { data: badges } = useQuery({
    queryKey: ['dashboard-badges'],
    queryFn: async () => {
      const { data } = await api.get('/analytics/badges')
      return data as Record<string, number>
    },
    enabled: !!user?.clinicId,
  })

  // Lab notifications (F2)
  const { totalAlerts: labAlertCount } = useLabNotifications(true)

  const { data: unreadCount } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => {
      const { data } = await api.get('/notifications/unread-count')
      return data.count as number
    },
  })

  const { data: notifications, isLoading: notifLoading } = useQuery({
    queryKey: ['notifications', 'dropdown', { limit: 5 }],
    queryFn: async () => {
      const { data } = await api.get('/notifications', { params: { limit: 5 } })
      return data as Array<{ id: string; type: string; title: string; message: string; createdAt: string; actionUrl?: string; actionLabel?: string; isRead?: boolean }>
    },
    enabled: notifOpen,
  })

  return (
    <div className="flex h-screen overflow-hidden bg-surface-0">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-surface-1 border-r border-border flex flex-col flex-shrink-0 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-border-subtle">
          <img src="/logo.png" alt="SmartDental" className="w-7 h-7 mr-2.5" />
          <span className="text-xl font-bold text-primary-700 tracking-tight">SmartDental</span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-6">
          <Link
            to="/"
            data-tour="dashboard"
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              location.pathname === '/'
                ? 'text-primary-700 bg-primary-50'
                : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
            }`}
          >
            <Home className={`w-5 h-5 ${location.pathname === '/' ? 'text-primary-600' : 'text-text-tertiary'}`} />
            Inicio
          </Link>
          {visibleSections.map((section) => (
            <div key={section.label}>
              <p className="px-3 text-[11px] font-semibold text-text-tertiary uppercase tracking-wider mb-1.5">
                {section.label}
              </p>
              {section.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                const baseBadgeValue = item.badgeKey ? (badges?.[item.badgeKey] ?? 0) : 0
                const badgeValue = item.path === '/lab' ? (baseBadgeValue || labAlertCount) : baseBadgeValue
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-tour={`nav-${item.path.replace(/^\//, '')}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary'
                    }`}
                  >
                    <item.icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-text-tertiary'}`} />
                    {item.label}
                    {badgeValue > 0 && (
                      <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        badgeValue > 2
                          ? 'bg-warning-50 text-warning-700'
                          : 'bg-primary-50 text-primary-700'
                      }`}>
                        {badgeValue}
                      </span>
                    )}
                  </Link>
                )
              })}
            </div>
          ))}
        </nav>

        {/* User mini profile + dropdown */}
        <div className="p-3 border-t border-border-subtle relative" ref={userMenuRef}>
          {userMenuOpen && (
            <div className="absolute bottom-full mb-2 left-3 right-3 bg-surface-1 border border-border rounded-lg shadow-lg overflow-hidden z-50">
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  navigate('/profile')
                }}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary text-left"
              >
                <UserCircle className="w-4 h-4 text-text-tertiary" />
                Mi perfil
              </button>
              <button
                onClick={() => {
                  setUserMenuOpen(false)
                  navigate('/settings')
                }}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary text-left"
              >
                <Settings className="w-4 h-4 text-text-tertiary" />
                Configuracion
              </button>
              <button
                onClick={async () => {
                  setUserMenuOpen(false)
                  try {
                    await api.post('/auth/logout')
                  } catch {
                    // Ignore logout errors
                  }
                  logout()
                  navigate('/login')
                }}
                className="flex items-center gap-2 w-full px-3 py-2.5 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary text-left border-t border-border-subtle"
              >
                <LogOut className="w-4 h-4 text-text-tertiary" />
                Cerrar sesion
              </button>
            </div>
          )}
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-2 cursor-pointer transition-colors w-full"
          >
            <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
              {userInitials}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-text-primary truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-text-tertiary truncate">
                {user?.specialty || user?.role}
              </p>
            </div>
            <ChevronDown className={`w-4 h-4 text-text-tertiary transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-surface-1 border-b border-border flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-surface-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5 text-text-secondary" />
            </button>
            <div>
              <h1 className="text-lg lg:text-xl font-bold text-text-primary">
                {currentClinic?.name || 'Dashboard'}
              </h1>
              <p className="text-xs text-text-tertiary hidden sm:block">
                {new Date().toLocaleDateString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Command palette trigger - desktop */}
            <button
              data-tour="command-palette"
              className="hidden md:flex items-center bg-surface-0 border border-border rounded-lg px-3 py-2 w-72 text-left hover:border-primary-300 hover:bg-surface-1 transition-colors"
              onClick={() => setPaletteOpen(true)}
            >
              <Search className="w-4 h-4 text-text-tertiary mr-2 flex-shrink-0" />
              <span className="text-sm text-text-tertiary flex-1">Buscar o ejecutar accion...</span>
              <kbd className="hidden lg:inline-block text-[10px] font-mono text-text-tertiary bg-surface-2 px-1.5 py-0.5 rounded border border-border">
                Ctrl K
              </kbd>
            </button>
            {/* Command palette trigger - mobile */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-surface-2"
              onClick={() => setPaletteOpen(true)}
              aria-label="Abrir paleta de comandos"
            >
              <Search className="w-5 h-5 text-text-secondary" />
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                data-tour="notifications"
                className="relative p-2 rounded-lg hover:bg-surface-2 transition-colors"
                onClick={() => setNotifOpen((v) => !v)}
              >
                <Bell className="w-5 h-5 text-text-secondary" />
                {unreadCount ? (
                  <span className="absolute top-1 right-1 min-w-[1rem] h-4 bg-error-500 text-white text-[10px] font-bold px-1 rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                ) : null}
              </button>

              {notifOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-surface-1 border border-border rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
                    <span className="text-sm font-semibold text-text-primary">Notificaciones</span>
                    {unreadCount ? (
                      <span className="text-xs text-text-tertiary">{unreadCount} sin leer</span>
                    ) : null}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifLoading ? (
                      <div className="px-4 py-3 space-y-3">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    ) : notifications && notifications.length > 0 ? (
                      notifications.map((n) => (
                        <div key={n.id} className="px-4 py-3 hover:bg-surface-2 transition-colors border-b border-border-subtle last:border-0">
                          <div className="flex items-start gap-2">
                            {!n.isRead && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-text-primary">{n.title}</p>
                              <p className="text-xs text-text-secondary mt-0.5">{n.message}</p>
                              <p className="text-[10px] text-text-tertiary mt-1">
                                {new Date(n.createdAt).toLocaleString('es-ES')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-sm text-text-tertiary">
                        {unreadCount ? 'No hay notificaciones recientes' : 'No hay notificaciones'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Banner modo demo read-only */}
        {isReadOnly && (
          <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 lg:px-6 py-2.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm font-medium text-amber-800 truncate">
                Modo Demo — Solo lectura. Los datos no se pueden modificar.
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  await api.post('/auth/logout')
                } catch {
                  // Ignore logout errors
                }
                logout()
                window.location.href = 'https://smartdental.es'
              }}
              className="flex-shrink-0 text-xs font-semibold text-amber-700 hover:text-amber-900 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              Salir de la demo
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </div>

        {/* Footer */}
        <footer className="flex-shrink-0 border-t border-border bg-surface-1 py-3 px-4 lg:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-text-tertiary">
            <span>SmartDental — ERP Dental Integral</span>
            <div className="flex items-center gap-3">
              <Link to="/privacidad" target="_blank" className="hover:text-primary-600 transition-colors">
                Privacidad
              </Link>
              <Link to="/aviso-legal" target="_blank" className="hover:text-primary-600 transition-colors">
                Aviso legal
              </Link>
              <Link to="/cookies" target="_blank" className="hover:text-primary-600 transition-colors">
                Cookies
              </Link>
            </div>
          </div>
        </footer>
      </main>

      {/* Command Palette */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* AI Assistant chat */}
      <AssistantChat />

      {/* Global alerts (mounted, no UI) */}
      <ReviewAlert />
      <LowStockGlobalAlert />

      {/* Contextual help */}
      <ContextualHelpButton />
    </div>
  )
}
